/* eslint-disable jira/integration/test-id-by-folder-structure */

/** @jsx jsx */
import React, { useCallback, type ReactNode } from 'react';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Form from '@atlaskit/form/form';
import { FormFooter } from '@atlaskit/form/form-footer';
import { FormSection } from '@atlaskit/form/form-section';
import Field from '@atlaskit/form/field';
import { useFormState } from '@atlaskit/form/use-form-state';
import { Label } from '@atlaskit/form/label';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import Select from '@atlaskit/select/select';
import type { ValueType } from '@atlaskit/select/types';
import Link from '@atlaskit/link/link';
import Spinner from '@atlaskit/spinner/spinner';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import SectionMessage from '@atlaskit/section-message/section-message';
import { fg } from '@atlassian/jira-feature-gating';
import { useRouterActions } from '@atlassian/react-resource-router';
import { defineMessage, useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type {
	ApplicationLinkV3Data,
	ApplicationLinkV3Simple,
	ApplicationLinkV3Status,
	RestApplinkOAuthStatus,
	RestOAuthConfig,
} from './CommonApplicationLinkCreationEntity.tsx';
import { ApplicationUrl, DisplayName, DisplayUrl } from './DisplayUrl.tsx';
import {
	APPLINKS_LIST_PAGE_URL,
	APPLINKS_LIST_PAGE_URL_LEGACY,
} from './applinksListPageUrl.constants.tsx';
import { putApplinkOAuth1Config, updateApplinkV3 } from './fetchApplinkManifestForUrl.tsx';
import { lookupTypeFromV3Data, type TypeDefinition } from './TypeDefinition.types.tsx';
import { getApplinksAdminUrl } from './getApplinksAdminUrl.tsx';
import {
	AUTH_DISABLED,
	AUTH_OAUTH1,
	AUTH_OAUTH1_IMPERSONATION,
	AUTH_OAUTH2,
	getAuthLevel,
	type AuthType,
} from './getAuthLevel.tsx';
import { formatBackendError } from './formatBackendError.tsx';

import ArrowLeftDisabled from './arrow-left-disabled.svg';
import ArrowLeftMatch from './arrow-left-match.svg';
import ArrowLeftMismatch from './arrow-left-mismatch.svg';
import ArrowRightDisabled from './arrow-right-disabled.svg';
import ArrowRightMatch from './arrow-right-match.svg';
import ArrowRightMismatch from './arrow-right-mismatch.svg';

type ArrowState = 'disabled' | 'match' | 'mismatch';

const Arrows: {
	incoming: Record<ArrowState, string>;
	outgoing: Record<ArrowState, string>;
} = {
	incoming: {
		disabled: ArrowLeftDisabled,
		match: ArrowLeftMatch,
		mismatch: ArrowLeftMismatch,
	},
	outgoing: {
		disabled: ArrowRightDisabled,
		match: ArrowRightMatch,
		mismatch: ArrowRightMismatch,
	},
};

const arrowDisabledHoverTextMessage = defineMessage({
	defaultMessage: 'Authentication is disabled',
	id: 'applinks.administration.ui.edit.arrow-disabled-alt-text',
	description:
		'Accessible label for the authentication status arrow. Shown when the arrow is greyed out.',
});

const arrowMatchHoverTextMessage = defineMessage({
	defaultMessage: 'Authentication is enabled',
	id: 'applinks.administration.ui.edit.arrow-match-alt-text',
	description:
		'Accessible label for the authentication status arrow. Shown when the arrow is green (enabled).',
});

const arrowMismatchHoverTextMessage = defineMessage({
	defaultMessage: 'Authentication is mismatched between local and remote',
	id: 'applinks.administration.ui.edit.arrow-mismatch-alt-text',
	description:
		'Accessible label for the authentication status arrow. Shown when the arrow is red with a cross (error).',
});

const authTableRowDirectionIncomingMessage = defineMessage({
	defaultMessage: 'Incoming',
	id: 'applinks.administration.ui.edit.direction-incoming',
	description: 'Table row heading for information about incoming authentication.',
});

const authTableRowDirectionOutgoingMessage = defineMessage({
	defaultMessage: 'Outgoing',
	id: 'applinks.administration.ui.edit.direction-outgoing',
	description: 'Table row heading for information about outgoing authentication.',
});

const authTableColumnDirectionHeadingMessage = defineMessage({
	defaultMessage: 'Direction',
	id: 'applinks.administration.ui.edit.direction-heading',
	description: 'Table column heading for the direction of authentication.',
});

const authTableColumnLocalAuthenticationHeadingMessage = defineMessage({
	defaultMessage: 'Local authentication',
	id: 'applinks.administration.ui.edit.local-authentication-heading',
	description: 'Table column heading for local authentication.',
});

const authTableColumnRemoteAuthenticationHeadingMessage = defineMessage({
	defaultMessage: 'Remote authentication',
	id: 'applinks.administration.ui.edit.remote-authentication-heading',
	description: 'Table column heading for remote authentication.',
});

const impersonationRemovedTitleMessage = defineMessage({
	defaultMessage: 'OAuth with impersonation is not supported',
	id: 'applinks.administration.ui.edit.impersonation-removed-title',
	description: 'Title for the error message displayed when trying to use OAuth with impersonation.',
});

const impersonationRemovedDescriptionMessage = defineMessage({
	defaultMessage:
		'Support for OAuth with impersonation <link>has been removed</link>. Please select a different authentication method.',
	id: 'applinks.administration.ui.edit.impersonation-removed-description',
	description: 'Error message displayed when trying to use OAuth with impersonation.',
});

const unsupportedAuthenticationTypeMessage = defineMessage({
	defaultMessage:
		'Invalid authentication type "{invalidAuthType}" used. Please select a different authentication method.',
	id: 'applinks.administration.ui.edit.unsupported-authentication-type',
	description:
		'Error message displayed when an unsupported authentication type is used. {invalidAuthType} will be either "OAuth 2.0" or "OAuth with impersonation".',
});

const linkDetailsSectionHeadingMessage = defineMessage({
	defaultMessage: 'Link details',
	id: 'applinks.administration.ui.edit.link-details-section-heading',
	description: 'Heading for the section containing application link details on the edit page.',
});

const connectionsTitleMessage = defineMessage({
	defaultMessage: 'Connections',
	id: 'applinks.administration.ui.edit.connections-section-header',
	description: 'Heading for the section containing OAuth 1.0 connection details on the edit page.',
});

const goToRemoteSiteLinkMessage = defineMessage({
	defaultMessage: 'Go to {displayUrl}',
	id: 'applinks.administration.ui.edit.go-to-remote-site-link',
	description: 'Clickable link which navigates the user to the site at URL {displayUrl}',
});

const applinkStatusLoadingTextMessage = defineMessage({
	defaultMessage: 'Loading application link status',
	id: 'applinks.administration.ui.edit.applink-status-loading-text',
	description:
		'Message that the application link status is currently being loaded, and will be displayed soon.',
});

const formCancelButtonTextMessage = defineMessage({
	defaultMessage: 'Cancel',
	id: 'applinks.administration.ui.edit.form-cancel-button-text',
	description: 'Label for button which discards the changes made on the edit page.',
});

const formSaveButtonTextMessage = defineMessage({
	defaultMessage: 'Save',
	id: 'applinks.administration.ui.edit.form-save-button-text',
	description: 'Label for button which saves the changes made on the edit page.',
});

const authTypeImpersonationMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-impersonation',
	defaultMessage: 'OAuth with impersonation',
	description:
		'Shown for the auth type "OAuth with impersonation" - please translate impersonation but do not translate "OAuth"',
});

const authTypeDisabledMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-disabled',
	defaultMessage: 'Disabled',
	description:
		'The text is shown as a label in the authentication level mismatch detail panel to indicate that authentication is disabled on one side of the application link connection.',
});

const authTypeUnknownMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-unknown',
	defaultMessage: 'Unknown',
	description: 'Shown when the auth type cannot be determined',
});

const unknownErrorTextMessage = defineMessage({
	defaultMessage: 'We encountered an unknown error.',
	id: 'applinks.administration.ui.unknown-error',
	description:
		'A generic error message for when something goes wrong, but without any further detail.',
});

export const SUPPORTED_AUTH_TYPES: AuthType[] = [
	AUTH_OAUTH1,
	AUTH_OAUTH1_IMPERSONATION,
	AUTH_DISABLED,
];

type AuthOption = { label: string; value: AuthType };

const styles = cssMap({
	growField: {
		flexGrow: 1,
	},
	dividerStyle: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		width: '100%',
		marginTop: token('space.200'),
	},
	formSectionInner: {
		marginTop: token('space.200'),
	},
	fieldNegativeMargin: {
		// Workaround for https://product-fabric.atlassian.net/browse/DSP-5423
		marginTop: token('space.negative.100'),
	},
	colDirection: {
		width: '20%',
	},
	colOutgoing: {
		width: '40%',
	},
	colIncoming: {
		width: '40%',
	},
});

export const matchOAuthConfigs = (
	localAuthLevel?: AuthType,
	remoteAuthLevel?: AuthType,
): ArrowState => {
	if (!localAuthLevel || !remoteAuthLevel) {
		return 'mismatch';
	}

	if (localAuthLevel === remoteAuthLevel) {
		if (localAuthLevel === AUTH_DISABLED) {
			return 'disabled';
		}
		return 'match';
	}
	return 'mismatch';
};

const ApplinksConnectedArrow = (props: { isIncoming: boolean; state: ArrowState }) => {
	const { formatMessage } = useIntl();

	const formatAltText = useCallback(() => {
		switch (props.state) {
			case 'match':
				return formatMessage(arrowMatchHoverTextMessage);
			case 'mismatch':
				return formatMessage(arrowMismatchHoverTextMessage);
			case 'disabled':
			default:
				return formatMessage(arrowDisabledHoverTextMessage);
		}
	}, [formatMessage, props.state]);

	// Render the appropriate arrow icon based on the connection state
	return props.isIncoming ? (
		<Tooltip content={formatAltText()}>
			<img height="16px" src={Arrows.incoming[props.state]} alt={formatAltText()} />
		</Tooltip>
	) : (
		<Tooltip content={formatAltText()}>
			<img height="16px" src={Arrows.outgoing[props.state]} alt={formatAltText()} />
		</Tooltip>
	);
};

const buildLocalisedAuthTypes = (
	formatMessage: (desc: MessageDescriptor) => string,
	selectableOnly?: boolean,
) => {
	const oauthLabel = 'OAuth';
	const impersonationLabel = formatMessage(authTypeImpersonationMessage);
	const disabledLabel = formatMessage(authTypeDisabledMessage);
	const oauth2Label = 'OAuth 2.0';

	if (selectableOnly) {
		return [
			{ label: oauthLabel, value: AUTH_OAUTH1 },
			{ label: disabledLabel, value: AUTH_DISABLED },
		];
	}

	return [
		{ label: oauthLabel, value: AUTH_OAUTH1 },
		{ label: impersonationLabel, value: AUTH_OAUTH1_IMPERSONATION },
		{ label: disabledLabel, value: AUTH_DISABLED },
		{ label: oauth2Label, value: AUTH_OAUTH2 },
	];
};

const LocalAuthDetails = (props: {
	isIncoming: boolean;
	state: ArrowState;
	name: string;
	defaultOAuthConfig?: RestOAuthConfig;
}) => {
	const { formatMessage } = useIntl();

	const getAuthOptions = useCallback(() => {
		return buildLocalisedAuthTypes(formatMessage, true);
	}, [formatMessage]);

	const getDefaultOption = useCallback(() => {
		const options = buildLocalisedAuthTypes(formatMessage);
		const authLevel = getAuthLevel(props.defaultOAuthConfig);

		return options.find((option) => option.value === authLevel) || options[0];
	}, [formatMessage, props.defaultOAuthConfig]);

	return (
		<Inline space="space.200" alignBlock="center">
			<Box xcss={styles.growField}>
				<Box xcss={styles.fieldNegativeMargin}>
					<Field<ValueType<AuthOption>> name={props.name} defaultValue={getDefaultOption()}>
						{({ fieldProps }) => (
							<Select
								testId={`applinks-administration.ui.edit.oauth1.local-auth-select.${props.isIncoming ? 'incoming' : 'outgoing'}`}
								{...fieldProps}
								inputId={props.name}
								options={getAuthOptions()}
							/>
						)}
					</Field>
				</Box>
			</Box>
			<ApplinksConnectedArrow isIncoming={props.isIncoming} state={props.state} />
		</Inline>
	);
};

const RemoteAuthDetails = (props: {
	type: TypeDefinition;
	oauthConfig?: RestOAuthConfig;
	url: string;
}) => {
	const { formatMessage } = useIntl();

	const getAuthDescription = useCallback(() => {
		const options = buildLocalisedAuthTypes(formatMessage);
		const authLevel = getAuthLevel(props.oauthConfig);

		return options.find((option) => option.value === authLevel);
	}, [formatMessage, props.oauthConfig]);

	return (
		<Inline space="space.300">
			<Inline space="space.100">
				{props.type.icon}
				<Link appearance="subtle" href={props.url}>
					{props.type.name}
				</Link>
			</Inline>
			<Text>{getAuthDescription()?.label ?? formatMessage(authTypeUnknownMessage)}</Text>
		</Inline>
	);
};

const ConfigureAuthTable = (props: {
	applink: ApplicationLinkV3Data;
	localAuth?: RestApplinkOAuthStatus;
	remoteAuth?: RestApplinkOAuthStatus;
}) => {
	const { formatMessage } = useIntl();

	const formState = useFormState<OAuth1EditForm>({
		values: true,
	});

	const getApplinkType = useCallback(() => {
		return lookupTypeFromV3Data(props.applink);
	}, [props.applink]);

	const getOutgoingMatchState = useCallback(() => {
		return matchOAuthConfigs(
			formState?.values.outgoingAuth?.value ?? undefined,
			getAuthLevel(props.remoteAuth?.incoming),
		);
	}, [props.remoteAuth, formState?.values.outgoingAuth]);

	const getIncomingMatchState = useCallback(() => {
		return matchOAuthConfigs(
			formState?.values.incomingAuth?.value ?? undefined,
			getAuthLevel(props.remoteAuth?.outgoing),
		);
	}, [props.remoteAuth, formState?.values.incomingAuth]);

	const showOAuthImpersonationError = useCallback(() => {
		const isLocalAuthImpersonation =
			formState?.values.incomingAuth?.value === AUTH_OAUTH1_IMPERSONATION;
		const isRemoteAuthImpersonation =
			formState?.values.outgoingAuth?.value === AUTH_OAUTH1_IMPERSONATION;

		if (isLocalAuthImpersonation || isRemoteAuthImpersonation) {
			return (
				<SectionMessage title={formatMessage(impersonationRemovedTitleMessage)} appearance="error">
					{formatMessage(impersonationRemovedDescriptionMessage, {
						link: (chunks: ReactNode[]) => (
							<Link
								href="https://confluence.atlassian.com/jirakb/end-of-support-for-2loi-application-links-950806698.html"
								target="_blank"
							>
								{chunks}
							</Link>
						),
					})}
				</SectionMessage>
			);
		}

		return null;
	}, [formState?.values.incomingAuth?.value, formState?.values.outgoingAuth?.value, formatMessage]);

	return (
		<Stack>
			{showOAuthImpersonationError()}
			<table>
				<colgroup>
					<col css={styles.colDirection} />
					<col css={styles.colOutgoing} />
					<col css={styles.colIncoming} />
				</colgroup>
				<thead>
					<tr>
						<th>{formatMessage(authTableColumnDirectionHeadingMessage)}</th>
						<th>{formatMessage(authTableColumnLocalAuthenticationHeadingMessage)}</th>
						<th>{formatMessage(authTableColumnRemoteAuthenticationHeadingMessage)}</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>
							<Label htmlFor="outgoingAuth">
								{formatMessage(authTableRowDirectionOutgoingMessage)}
							</Label>
						</td>
						<td>
							<LocalAuthDetails
								isIncoming={false}
								state={getOutgoingMatchState()}
								name="outgoingAuth"
								defaultOAuthConfig={props.localAuth?.outgoing}
							/>
						</td>
						<td>
							<RemoteAuthDetails
								type={getApplinkType()}
								url={props.applink.displayUrl}
								oauthConfig={props.remoteAuth?.incoming}
							/>
						</td>
					</tr>
					<tr>
						<td>
							<Label htmlFor="incomingAuth">
								{formatMessage(authTableRowDirectionIncomingMessage)}
							</Label>
						</td>
						<td>
							<LocalAuthDetails
								isIncoming
								state={getIncomingMatchState()}
								name="incomingAuth"
								defaultOAuthConfig={props.localAuth?.incoming}
							/>
						</td>
						<td>
							<RemoteAuthDetails
								type={getApplinkType()}
								url={props.applink.displayUrl}
								oauthConfig={props.remoteAuth?.outgoing}
							/>
						</td>
					</tr>
				</tbody>
			</table>
		</Stack>
	);
};

type OAuth1EditForm = {
	displayName: string;
	rpcUrl: string;
	displayUrl: string;
	outgoingAuth: ValueType<AuthOption>;
	incomingAuth: ValueType<AuthOption>;
};

type ApplinksOAuth1EditPageContentsProps = {
	applink: ApplicationLinkV3Data;
	status: ApplicationLinkV3Status | null;
	onDisplayError: (err: string) => void;
};

const buildOAuthConfigFromOption = (option: ValueType<AuthOption>): RestOAuthConfig => {
	if (option?.value === AUTH_OAUTH1) {
		return {
			enabled: true,
			twoLoEnabled: true,
			twoLoImpersonationEnabled: false,
			oAuth2ThreeLoEnabled: false,
		};
	}
	if (option?.value === AUTH_OAUTH1_IMPERSONATION) {
		return {
			enabled: true,
			twoLoEnabled: true,
			twoLoImpersonationEnabled: true,
			oAuth2ThreeLoEnabled: false,
		};
	}
	return {
		enabled: false,
		twoLoEnabled: false,
		twoLoImpersonationEnabled: false,
		oAuth2ThreeLoEnabled: false,
	};
};

const ApplinksOAuth1EditPageContents = (
	props: ApplinksOAuth1EditPageContentsProps,
): JSX.Element => {
	const { push } = useRouterActions();

	const { formatMessage } = useIntl();

	const getListPageUrl = useCallback(() => {
		if (fg('applinks-933-experimental-list-page')) {
			return APPLINKS_LIST_PAGE_URL;
		}
		return APPLINKS_LIST_PAGE_URL_LEGACY;
	}, []);

	const onFormSubmit = useCallback(
		async (data: OAuth1EditForm) => {
			try {
				const supportedAuthTypes = buildLocalisedAuthTypes(formatMessage, true);
				const isOutgoingAuthSupported = supportedAuthTypes.some(
					(type) => type.value === data.outgoingAuth?.value,
				);
				const isIncomingAuthSupported = supportedAuthTypes.some(
					(type) => type.value === data.incomingAuth?.value,
				);

				if (!isOutgoingAuthSupported) {
					throw new Error(
						formatMessage(unsupportedAuthenticationTypeMessage, {
							invalidAuthType: data.outgoingAuth?.label,
						}),
					);
				}

				if (!isIncomingAuthSupported) {
					throw new Error(
						formatMessage(unsupportedAuthenticationTypeMessage, {
							invalidAuthType: data.incomingAuth?.label,
						}),
					);
				}

				const applinkUpdate: ApplicationLinkV3Simple = {
					id: props.applink.id,
					type: props.applink.type,
					system: props.applink.system,
					primary: props.applink.primary,
					isExtraAuthRequired: props.applink.isExtraAuthRequired,

					// Only change these properties:
					name: data.displayName,
					displayUrl: data.displayUrl,
					rpcUrl: data.rpcUrl,
				};

				await updateApplinkV3(props.applink.id, applinkUpdate);

				await putApplinkOAuth1Config(props.applink.id, {
					outgoing: buildOAuthConfigFromOption(data.outgoingAuth),
					incoming: buildOAuthConfigFromOption(data.incomingAuth),
				});

				await push(getListPageUrl());
			} catch (err) {
				const maybeError = err instanceof Error ? err : undefined;
				log.safeErrorWithoutCustomerData(
					'applinks.administration.edit.oauth1.submitEditForm',
					'Failed to update OAuth 1.0 applink',
					maybeError,
				);
				if (fg('applinks-1093-redirect-error-fix')) {
					props.onDisplayError(
						formatBackendError(maybeError, formatMessage(unknownErrorTextMessage)),
					);
				} else {
					props.onDisplayError(
						maybeError ? maybeError.message : formatMessage(unknownErrorTextMessage),
					);
				}
				return err;
			}
		},
		[formatMessage, getListPageUrl, props, push],
	);

	return (
		<>
			<Form<OAuth1EditForm> onSubmit={onFormSubmit}>
				{({ formProps, submitting }) => (
					<form {...formProps}>
						<Stack space="space.200">
							<FormSection title={formatMessage(linkDetailsSectionHeadingMessage)}>
								<Stack space="space.200" xcss={styles.formSectionInner}>
									<DisplayName name="displayName" defaultName={props.applink.name} />
									<DisplayUrl name="displayUrl" defaultUrl={props.applink.displayUrl} />
									<ApplicationUrl
										name="rpcUrl"
										defaultUrl={props.applink.rpcUrl}
										disabled={false}
									/>
								</Stack>
							</FormSection>
							<hr role="presentation" css={styles.dividerStyle} />
							<FormSection title={formatMessage(connectionsTitleMessage)}>
								{props.status ? (
									<Stack space="space.200" xcss={styles.formSectionInner}>
										<ConfigureAuthTable
											applink={props.applink}
											localAuth={props.status.localAuthentication}
											remoteAuth={props.status.remoteAuthentication}
										/>
										<Box>
											<Link
												href={getApplinksAdminUrl(props.applink.displayUrl, props.applink.type)}
												target="_blank"
											>
												{formatMessage(goToRemoteSiteLinkMessage, {
													displayUrl: props.applink.displayUrl,
												})}
											</Link>
										</Box>
									</Stack>
								) : (
									<Inline space="space.200" xcss={styles.formSectionInner}>
										<Spinner />
										<Text>{formatMessage(applinkStatusLoadingTextMessage)}</Text>
									</Inline>
								)}
							</FormSection>

							<FormFooter>
								<ButtonGroup>
									<LinkButton href={getListPageUrl()} appearance="subtle">
										{formatMessage(formCancelButtonTextMessage)}
									</LinkButton>
									<Button
										testId="applinks-administration.ui.edit.oauth1.submit-button"
										type="submit"
										appearance="primary"
										isLoading={submitting}
									>
										{formatMessage(formSaveButtonTextMessage)}
									</Button>
								</ButtonGroup>
							</FormFooter>
						</Stack>
					</form>
				)}
			</Form>
		</>
	);
};

export default ApplinksOAuth1EditPageContents;
